/**
 * The one place a plan limit is announced from.
 *
 * Any request refused with a quota payload (403 `{ quotaType, … }`) lands here
 * through `sendAuthGuardedRequest`, and so does any page that already knows a
 * limit is spent (the "add product" button at 10 of 10). One dialog host,
 * mounted once in the app, shows it — so every limit reads the same way,
 * whichever page hit it.
 *
 * Kept free of React so the request layer can import it without dragging a
 * component tree along.
 */
import { create } from 'zustand';

import { PlanLimitPayload, readPlanError } from './plan-limits';

/** The same limit is not announced twice within this window (a page that retries, or polls). */
const REPEAT_WINDOW_MS = 60_000;

interface HistoryClamp {
  /** YYYY-MM-DD: the earliest day the plan let the API read. */
  clampedTo: string;
  at: number;
}

interface PlanLimitState {
  /** The limit on screen, or null. */
  current: PlanLimitPayload | null;
  /** When a limit was last announced — the page-level snackbars step aside for it. */
  lastShownAt: number;
  /** quotaType → when it was dismissed, so a retry does not re-open it at once. */
  dismissed: Record<string, number>;
  /** The latest `historyClampedTo` the analytics API returned. */
  clamp: HistoryClamp | null;
  show: (payload: PlanLimitPayload) => boolean;
  close: () => void;
  noteClamp: (clampedTo: string) => void;
}

export const usePlanLimitStore = create<PlanLimitState>((set, get) => ({
  current: null,
  lastShownAt: 0,
  dismissed: {},
  clamp: null,
  show: (payload) => {
    // Upgrading would not help (the account is not activated yet): not ours to say.
    if (!payload?.quotaType || payload.upgrade === false || payload.quotaType === 'activation') return false;
    const state = get();
    if (state.current) return false;
    const dismissedAt = state.dismissed[payload.quotaType];
    if (dismissedAt && Date.now() - dismissedAt < REPEAT_WINDOW_MS) return false;
    set({ current: payload, lastShownAt: Date.now() });
    return true;
  },
  close: () => {
    const { current, dismissed } = get();
    set({ current: null, dismissed: current ? { ...dismissed, [current.quotaType]: Date.now() } : dismissed });
  },
  noteClamp: (clampedTo) => {
    if (typeof clampedTo !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(clampedTo)) return;
    set({ clamp: { clampedTo: clampedTo.slice(0, 10), at: Date.now() } });
  },
}));

/** Show the unlock dialog for a limit. Returns whether it opened (false if one is already up, or it was just dismissed). */
export const openUnlockDialog = (payload: PlanLimitPayload) => usePlanLimitStore.getState().show(payload);

/**
 * For the request layer: announce a refused request if it carries a quota
 * payload. Returns true when the error was a plan limit, shown or not.
 */
export function notifyPlanLimit(error: unknown): boolean {
  const payload = readPlanError(error);
  if (!payload) return false;
  openUnlockDialog(payload);
  return true;
}

/** For the request layer: remember that a response was cut to the plan's history window. */
export function notePlanResponse(response: unknown): void {
  if (!response || typeof response !== 'object') return;
  const clamped = (response as Record<string, unknown>).historyClampedTo;
  if (typeof clamped === 'string') usePlanLimitStore.getState().noteClamp(clamped);
}

/** Whether a limit was announced in the last `ms` — page snackbars stay quiet then, so one refusal is said once. */
export const planLimitRecentlyShown = (ms = 3000) => Date.now() - usePlanLimitStore.getState().lastShownAt < ms;
